/**
 * Early email check against POST /v1/email-availability, which answers with
 * the code and message the submit gives the same address.
 */

import { validateEmail } from './validators.js';
import { getStepForField } from '../steps.js';
import { trackError } from '../analytics/signupAnalytics.js';

// A slow or failed check never holds the user on the step. The submit still judges the address.
export const EMAIL_CHECK_TIMEOUT_MS = 3000;

function withTimeout(promise, ms) {
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            () => { clearTimeout(timer); resolve(null); },
        );
    });
}

function currentEmail(state) {
    return String(state.email || '').trim();
}

// A refusal lands on `state.errors.email` the way a submit refusal does.
function applyRefusal(state, check) {
    const answer = check?.answer;
    if (!answer || answer.available !== false || currentEmail(state) !== check.email) {
        return false;
    }

    const error = { code: answer.code, message: answer.message };
    if (answer.meta) {
        error.meta = answer.meta;
    }
    state.errors = { ...state.errors, email: error };

    if (!check.tracked) {
        check.tracked = true;
        trackError(getStepForField('email'), 'email', answer.code);
    }

    return true;
}

/**
 * Check the current address, once per distinct value.
 *
 * @param {Object} state - the wizard component
 * @param {function(string): Promise<{available: boolean, code: ?string, message: ?string, meta: ?Object}>} request
 * @returns {Promise<boolean>} true when the server refuses the address
 */
export async function checkEmailAvailability(state, request) {
    const email = currentEmail(state);
    if (!validateEmail(email).valid) {
        return false;
    }

    if (state.emailCheck?.email !== email) {
        const check = { email, answer: null, tracked: false, result: null };
        check.result = withTimeout(request(email), EMAIL_CHECK_TIMEOUT_MS).then((answer) => {
            check.answer = answer;
            return answer;
        });
        state.emailCheck = check;
    }

    // Promises are not wrapped by Alpine's proxy, so `result` identifies the check.
    const pending = state.emailCheck.result;
    const answer = await pending;

    // No answer is not kept, so the next blur or Next asks again.
    if (!answer) {
        if (state.emailCheck?.result === pending) {
            state.emailCheck = null;
        }

        return false;
    }

    return state.emailCheck?.result === pending && applyRefusal(state, state.emailCheck);
}

/**
 * Apply a refusal that already arrived for the current address, without waiting.
 *
 * @returns {boolean} true when the address is refused
 */
export function applyKnownEmailRefusal(state) {
    const check = state.emailCheck;

    return Boolean(check) && check.email === currentEmail(state) && applyRefusal(state, check);
}
